// libraries.
import { toast } from 'react-toastify';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// slices.
import {
  setDefaultState,
  setShowModal,
} from '@domains/documents-review/store/slices/document-review-slice';
import useBusinessTypeWebSocket from '@domains/hermes/hooks/useBusinessTypeWebSocket';
import {
  getHermesDataBody,
  setHermesData,
} from '@domains/hermes/slices/hermesData.slice';
import { useSaveHermesMutation } from '@domains/hermes/services/hermes.endpoints';
import { getLeadDetailData } from '@domains/lead-detail/store/slices/lead-detail-slice';

// constants.
import { DROPDOWN_OPTIONS, LIEN_LIST_DATA } from './constants';
export { LIEN_LIST_DATA, DROPDOWN_OPTIONS };

export const defaultValuesMX = {
  complex: '',
  adminPrice: '',
  propertyType: '',
  medianZone: '',
  zipCode: '',
  committeePriceArea: '',
  committeeLandArea: '',
  committeeExpansionArea: '',
  rooms: '',
  baths: '',
  halfBaths: '',
  floor: '',
  elevator: '',
  hasGarageDoc: null,
  garage: '',
  yearsOld: '',
  parkingWithDoubleKeyOrDeed: null,
  accessoryAreas: [],
};

export const defaultValuesCO = {
  propertyType: '',
  elevators: '',
  hasAdmin: null,
  adminPrice: '',
  constructionDate: '',
  areaType: '',
  area: '',
  baths: '',
  floor: '',
  hasGarage: null,
  garage: '',
  hasTerrace: null,
  freeArea: '',
  liens: '',
  liensCategory: '',
  lenderBank: '',
};

export const useHandleMutationToast = (
  isError: boolean,
  isSuccess: boolean
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bodyForHermes = useSelector(getHermesDataBody);
  const leadDetailData = useSelector(getLeadDetailData);

  const [
    saveHermes,
    {
      isLoading: isLoadingHermes,
      isError: isErrorHermes,
      isSuccess: isSuccessHermes,
      data: dataHermes,
    },
  ] = useSaveHermesMutation();

  // Hook que maneja ambos flujos según business_type
  const { businessType, handleWebSocketSend } = useBusinessTypeWebSocket({
    whenSuccessGeoref: () => {
      navigate('/document-audit/tasks');
      toast.success('¡Hermes Data Actualizada! ');
      dispatch(setShowModal(false));
      dispatch(setDefaultState());
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error('Error al aprobar esta tarea', {
        toastId: 'errorApproveTask',
      });
    } else if (isSuccess) {
      toast.success('Tarea aprobada correctamente', {
        toastId: 'successApproveTask',
      });

      // Lógica según business_type:
      if (businessType === 'real_estate') {
        // Para real_estate: enviar directamente al websocket
        handleWebSocketSend();
      } else {
        // Para committee: usar el flujo normal (change_coordinates).
        // Sobrescribir coordenadas con las más recientes de leadDetailData en Redux
        // para evitar que un snapshot antiguo de bodyForHermes revierta las coords
        // actualizadas previamente por el módulo de Georeferenciación.
        if (bodyForHermes) {
          console.log('[Revisión] 📍 Coordenadas antes de enviar a Hermes:', {
            snapshot_lat: bodyForHermes.latitude,
            snapshot_lng: bodyForHermes.longitude,
            leadDetail_lat: leadDetailData?.latitude,
            leadDetail_lng: leadDetailData?.longitude,
            enviando_lat: leadDetailData?.latitude ?? bodyForHermes.latitude,
            enviando_lng: leadDetailData?.longitude ?? bodyForHermes.longitude,
          });
          saveHermes({
            ...bodyForHermes,
            latitude: leadDetailData?.latitude ?? bodyForHermes.latitude,
            longitude: leadDetailData?.longitude ?? bodyForHermes.longitude,
          })
            .unwrap()
            .catch((error: unknown) => {
              // El toast de error ya lo dispara el efecto de isErrorHermes;
              // este catch solo evita el unhandled promise rejection que
              // reportaba Sentry (PRICING-ROCKET-FRONT-2).
              console.error(
                'Error al enviar datos a Hermes (change_coordinates):',
                error
              );
            });
        }
      }
    }
  }, [isError, isSuccess, dispatch, businessType]);

  // Hermes status.
  useEffect(() => {
    if (isLoadingHermes) {
      toast.info('Enviando datos a Hermes...', {
        toastId: 'loadingHermes',
      });
    } else {
      toast.dismiss('loadingHermes');
    }
  }, [isLoadingHermes]);

  useEffect(() => {
    if (isErrorHermes) {
      toast.error('Error al enviar datos a Hermes', {
        toastId: 'errorHermes',
      });
    }
  }, [isErrorHermes]);

  useEffect(() => {
    if (isSuccessHermes) {
      // Siempre forzar validations: true para garantizar que useWebSocketGeoref
      // dispare el envío del WebSocket independientemente de lo que devuelva la API.
      const hermes = {
        ...dataHermes,
        validations: true,
      } as typeof dataHermes;

      console.log('[Revisión] 📦 hermesData despachado a Redux:', {
        deal_id: (hermes as any)?.deal_id,
        validations: (hermes as any)?.validations,
        country: (hermes as any)?.country,
      });

      toast.success('Enviando datos a sellers', {
        toastId: 'successHermes',
      });
      dispatch(setHermesData(hermes));
    }
  }, [isSuccessHermes, dataHermes]);
};
